import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Download, Eye, Loader2, Search, ChevronUp, ChevronDown } from "lucide-react";
import toast from "react-hot-toast";
import { FetchPendingUsersAction } from "../../Store/Actions/UserActions";
import { handleAuthError } from "../../utils/authUtils";
import { downloadUsersCsv } from "../../utils/downloadCsv";

const STATUS_STYLES = {
  ACTIVE: "bg-green-100 text-green-800",
  REJECTED: "bg-red-100 text-red-800",
  PENDING: "bg-yellow-100 text-yellow-800",
  BLOCKED: "bg-orange-100 text-orange-800",
};

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) return <ChevronUp size={12} className="text-gray-300 ml-1 inline" />;
  return sortDir === "asc"
    ? <ChevronUp size={12} className="text-themeColor ml-1 inline" />
    : <ChevronDown size={12} className="text-themeColor ml-1 inline" />;
};

const ApproveUsers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userState = useSelector((state) => state.user);
  const loader = userState?.loader ?? false;
  const pendingUsers = userState?.pendingUsers;
  const pendingUsersError = userState?.pendingUsersError ?? null;

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    try {
      setExporting(true);
      await downloadUsersCsv('pending');
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { dispatch(FetchPendingUsersAction()); }, [dispatch]);

  useEffect(() => {
    if (pendingUsersError) {
      if (handleAuthError(pendingUsersError, null, dispatch)) return;
      toast.error(pendingUsersError.message || "Failed to fetch pending users.");
    }
  }, [pendingUsersError, dispatch]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };

  const filtered = useMemo(() => {
    let list = [...(pendingUsers || [])];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((u) => {
        const name = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
        const org = u.nonProfitUser?.organizationName?.toLowerCase() || "";
        return u.email?.toLowerCase().includes(q) || name.includes(q) || org.includes(q);
      });
    }
    list.sort((a, b) => {
      let av, bv;
      if (sortField === "name") {
        av = (a.nonProfitUser?.organizationName || `${a.firstName || ""} ${a.lastName || ""}`).toLowerCase();
        bv = (b.nonProfitUser?.organizationName || `${b.firstName || ""} ${b.lastName || ""}`).toLowerCase();
      } else {
        av = new Date(a.createdAt).getTime();
        bv = new Date(b.createdAt).getTime();
      }
      return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
    return list;
  }, [pendingUsers, search, sortField, sortDir]);

  return (
    <div className="w-[95%] mx-auto">
      <div className="my-6">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-popinsSemiBold text-xl lg:text-2xl mb-1">User Approval</h1>
            <p className="text-black/50 font-popinsRegular text-sm">
              Review and approve pending user registrations
            </p>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-themeColor text-white text-sm font-popinsMedium hover:bg-opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
          >
            <Download size={15} />
            {exporting ? 'Exporting…' : 'Export to CSV'}
          </button>
        </div>

        {/* Search + Sort bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, org or email…"
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg font-popinsRegular focus:outline-none focus:border-themeColor"
            />
          </div>
        </div>

        {!loader && filtered.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <p className="text-textColor font-popinsMedium">
              {pendingUsers.length === 0 ? "No pending users found" : "No results match your search"}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th
                      className="px-3 py-3 text-left text-xs font-popinsSemiBold text-gray-600 uppercase tracking-wider cursor-pointer select-none w-[28%]"
                      onClick={() => toggleSort("name")}
                    >
                      Name / Org <SortIcon field="name" sortField={sortField} sortDir={sortDir} />
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-popinsSemiBold text-gray-600 uppercase tracking-wider w-[26%]">
                      Email
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-popinsSemiBold text-gray-600 uppercase tracking-wider w-[12%]">
                      Role
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-popinsSemiBold text-gray-600 uppercase tracking-wider w-[12%]">
                      Status
                    </th>
                    <th
                      className="px-3 py-3 text-left text-xs font-popinsSemiBold text-gray-600 uppercase tracking-wider cursor-pointer select-none w-[16%]"
                      onClick={() => toggleSort("createdAt")}
                    >
                      Joined <SortIcon field="createdAt" sortField={sortField} sortDir={sortDir} />
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-popinsSemiBold text-gray-600 uppercase tracking-wider w-[6%]">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loader && (
                    <tr>
                      <td colSpan={6} className="px-6 py-6 text-center">
                        <div className="flex justify-center items-center gap-2 text-gray-500">
                          <Loader2 className="animate-spin" size={20} />
                        </div>
                      </td>
                    </tr>
                  )}
                  {!loader && filtered.map((user) => {
                    const displayName = user.nonProfitUser?.organizationName
                      || `${user.firstName || ""} ${user.lastName || ""}`.trim()
                      || "—";
                    return (
                      <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-4">
                          <div className="text-sm font-popinsMedium text-gray-900 truncate max-w-[180px]">{displayName}</div>
                        </td>
                        <td className="px-3 py-4">
                          <div className="text-sm font-popinsRegular text-gray-600 truncate max-w-[160px]">{user.email}</div>
                        </td>
                        <td className="px-3 py-4">
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-popinsSemiBold rounded-full bg-blue-100 text-blue-800">
                            {user.roles?.[0]?.name || "N/A"}
                          </span>
                        </td>
                        <td className="px-3 py-4">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-popinsSemiBold rounded-full ${STATUS_STYLES[user.activationStatus] || "bg-gray-100 text-gray-800"}`}>
                            {user.activationStatus || "N/A"}
                          </span>
                        </td>
                        <td className="px-3 py-4">
                          <div className="text-sm font-popinsRegular text-gray-500">{formatDate(user.createdAt)}</div>
                        </td>
                        <td className="px-3 py-4 text-center">
                          <button
                            onClick={() => navigate(`/dashboard/user-detail/${user.id}?from=approve`)}
                            className="text-themeColor hover:text-[#3FC4E3] transition duration-300 ease-in-out transform hover:scale-110 flex items-center justify-center mx-auto"
                            title="View Details"
                          >
                            <Eye size={20} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApproveUsers;
